import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

const COLORS = [
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#6366F1",
];

export default function ProgressGraphModal({ onClose, history = [] }) {
  const chartData = useMemo(() => {
    return [...history]
      .sort((a, b) => {
        const da = new Date(a.confirmedAt || a.playDate || a.createdAt || a.updatedAt || a.date);
        const db = new Date(b.confirmedAt || b.playDate || b.createdAt || b.updatedAt || b.date);
        return da - db;
      })
      .map((h) => {
        const dateValue =
          h.confirmedAt ||
          h.playDate ||
          h.createdAt ||
          h.updatedAt ||
          h.date;

        return {
          day: dateValue
            ? new Date(dateValue).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })
            : "--",
          score:
            h.totalScore ??
            h.iqScore ??
            h.bestScore?.iqScore ??
            h.score ??
            0,
        };
      });
  }, [history]);

  const stats = useMemo(() => {
    const scores = chartData.map((d) => d.score);

    return {
      played: history.length,
      best: scores.length ? Math.max(...scores) : 0,
      average: scores.length
        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        : 0,
      latest: chartData.length ? chartData[chartData.length - 1].score : 0,
    };
  }, [history, chartData]);

  return (
    <div className="p-6 lg:p-8">

      <h2 className="text-2xl font-bold text-[#2B2140] mb-6 text-center">
        📈 Progress Graph
      </h2>

      {history.length > 0 ? (
        <>
          <div className="bg-gray-100 rounded-3xl p-4 border border-gray-200">

            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <XAxis
                  dataKey="day"
                  tick={{ fill: "#555", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "#555", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip
                  cursor={{ fill: "#F3EEFF" }}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #E5E7EB",
                    background: "#fff",
                  }}
                />

                <Bar
                  dataKey="score"
                  radius={[10, 10, 0, 0]}
                >
                  {chartData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={COLORS[i % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

          </div>

          <div className="grid grid-cols-2 gap-3 mt-5">

            <div className="bg-[#F8F5FF] rounded-2xl p-4 text-center border border-[#E9DFFF]">
              <div className="text-xl font-bold text-[#7C3AED]">
                {stats.played}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Days Played
              </div>
            </div>

            <div className="bg-[#F8F5FF] rounded-2xl p-4 text-center border border-[#E9DFFF]">
              <div className="text-xl font-bold text-[#7C3AED]">
                {stats.best}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Best Score
              </div>
            </div>

            <div className="bg-[#F8F5FF] rounded-2xl p-4 text-center border border-[#E9DFFF]">
              <div className="text-xl font-bold text-[#7C3AED]">
                {stats.average}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Average Score
              </div>
            </div>

            <div className="bg-[#F8F5FF] rounded-2xl p-4 text-center border border-[#E9DFFF]">
              <div className="text-xl font-bold text-[#7C3AED]">
                {stats.latest}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Latest Score
              </div>
            </div>

          </div>
        </>
      ) : (
        <div className="bg-gray-100 rounded-3xl h-80 flex items-center justify-center border border-dashed border-gray-300">
          <div className="text-center">
            <p className="text-5xl mb-3">📊</p>
            <p className="font-semibold text-gray-700">
              No progress yet
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Play your first game to start tracking your progress.
            </p>
          </div>
        </div>
      )}

      <button
        onClick={onClose}
        className="mt-8 w-full py-4 bg-[#7C3AED] hover:bg-[#6D28D9] transition-colors text-white font-semibold rounded-2xl"
      >
        Close
      </button>

    </div>
  );
}